import type { ElementType, FC, PropsWithChildren } from "react";

import classNames from "classnames";

import styles from "./Column.module.scss";

type ColumnWidth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export type ColumnProps = {
  className?: string;
  component?: ElementType;
  dataTestId?: string;
  // main breakpoints column width
  xs?: ColumnWidth; // 0 and up
  sm?: ColumnWidth; // 480px and up
  md?: ColumnWidth; // 768px and up
  lg?: ColumnWidth; // 1280px and up

  // Specific breakpoints column width
  specificBreakpoints?: {
    xSmall?: ColumnWidth; // 480px - 599px
    smallMedium?: ColumnWidth; // 600px - 767px
    smallLarge?: ColumnWidth; // 768px - 959px
    medium?: ColumnWidth; // 960px - 1069px
    mediumWide?: ColumnWidth; // 1070px - 1279px
    large?: ColumnWidth; // 1280px - 1439px
    largeMedium?: ColumnWidth; // 1440px - 1919px
  };
};

const mainBreakpoints = ["xs", "sm", "md", "lg"] as const;
const specificBreakpoints = [
  "xSmall",
  "smallMedium",
  "smallLarge",
  "medium",
  "mediumWide",
  "large",
  "largeMedium",
] as const;

export const gridBreakpoints = [...mainBreakpoints, ...specificBreakpoints];
export type Breakpoint = (typeof gridBreakpoints)[number];
export type ColumnWidthClassNames = Record<ColumnWidth, string>;
export type BreakpointColumnWidthClassNames = Record<Breakpoint, ColumnWidthClassNames>;

export const generateColumnWidthClassNames = (breakpoint: Breakpoint) => {
  const columnWidths: ColumnWidth[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  return columnWidths.reduce((acc, index) => {
    acc[index] = styles[`column-${breakpoint}-${index}`];
    return acc;
  }, {} as ColumnWidthClassNames);
};

export const generateColumnClassNames = (breakpoints: Breakpoint[]) => {
  return breakpoints.reduce((acc, breakpoint) => {
    acc[breakpoint] = generateColumnWidthClassNames(breakpoint);
    return acc;
  }, {} as BreakpointColumnWidthClassNames);
};

const columnClassNames = generateColumnClassNames(gridBreakpoints);

const DEFAULT_COLUMN_SIZE = 12;

export const Column: FC<PropsWithChildren<ColumnProps>> = ({
  children,
  component,
  className,
  dataTestId,
  md,
  sm,
  xs = DEFAULT_COLUMN_SIZE,
  lg,
  specificBreakpoints,
}) => {
  const Component = component || "div";
  return (
    <Component
      className={classNames(
        styles.column,
        className,
        md !== undefined ? columnClassNames.md[md] : undefined,
        sm !== undefined ? columnClassNames.sm[sm] : undefined,
        columnClassNames.xs[xs],
        lg !== undefined ? columnClassNames.lg[lg] : undefined,
        specificBreakpoints?.large !== undefined ? columnClassNames.large[specificBreakpoints.large] : undefined,
        specificBreakpoints?.largeMedium !== undefined
          ? columnClassNames.largeMedium[specificBreakpoints.largeMedium]
          : undefined,
        specificBreakpoints?.medium !== undefined ? columnClassNames.medium[specificBreakpoints.medium] : undefined,
        specificBreakpoints?.mediumWide !== undefined
          ? columnClassNames.mediumWide[specificBreakpoints.mediumWide]
          : undefined,
        specificBreakpoints?.xSmall !== undefined ? columnClassNames.xSmall[specificBreakpoints.xSmall] : undefined,
        specificBreakpoints?.smallMedium !== undefined
          ? columnClassNames.smallMedium[specificBreakpoints.smallMedium]
          : undefined,
        specificBreakpoints?.smallLarge !== undefined
          ? columnClassNames.smallLarge[specificBreakpoints.smallLarge]
          : undefined
      )}
      data-testid={dataTestId}
    >
      {children}
    </Component>
  );
};
